import { useRef, useState, type FormEvent } from 'react'

import { forgetRecent, loadRecents, type Recent } from '../lib/recents'

type Props = {
  busy: boolean
  onSubmit: (link: string) => void
  onStop: () => void
  hasSession: boolean
}

const HEX40 = /^[0-9a-f]{40}$/i

/**
 * A bare content id already works server-side, but showing it back as a full
 * acestream:// link is the only way the user can tell it was understood.
 */
function normalise(raw: string): string {
  const value = raw.trim()
  return HEX40.test(value) ? `acestream://${value.toLowerCase()}` : value
}

export function LinkForm({ busy, onSubmit, onStop, hasSession }: Props) {
  const [link, setLink] = useState('')
  const [recents, setRecents] = useState<Recent[]>(() => loadRecents())
  const inputRef = useRef<HTMLInputElement>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    const value = normalise(link)
    if (!value || busy) return
    setLink(value)
    onSubmit(value)
  }

  function replay(value: string) {
    setLink(value)
    onSubmit(value)
  }

  function clear() {
    setLink('')
    inputRef.current?.focus()
  }

  return (
    <section className="panel">
      <form className="linkform" onSubmit={submit}>
        <div className="linkform__field">
          <input
            ref={inputRef}
            className="linkform__input"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            onBlur={() => setLink((current) => normalise(current))}
            placeholder="acestream://… , un hash de 40 caràcters, un magnet o una URL .acelive"
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            inputMode="url"
          />
          {link && (
            <button className="linkform__clear" type="button" onClick={clear} aria-label="Esborra l'adreça">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
        <button className="btn btn--primary" type="submit" disabled={busy || !link.trim()}>
          {busy ? 'Obrint…' : 'Reprodueix'}
        </button>
        {hasSession && (
          <button className="btn" type="button" onClick={onStop}>
            Atura
          </button>
        )}
      </form>

      {recents.length > 0 && (
        <ul className="recents">
          {recents.map((recent) => (
            <li key={recent.link}>
              <button className="chip" onClick={() => replay(recent.link)} disabled={busy}>
                {recent.label}
              </button>
              <button
                className="chip__remove"
                aria-label={`Elimina ${recent.label}`}
                onClick={() => setRecents(forgetRecent(recent.link))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
